import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, IconButton, InputAdornment,
  InputLabel, Menu, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FailedIcon from '@mui/icons-material/Cancel';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import MenuIcon from '@mui/icons-material/Menu';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { apiClient } from 'utils/ApiClient';

interface ExecutionLogEntry {
  _id?: string;
  campaignId?: string;
  contactId: string;
  senderPhoneNumber?: string;
  receiverPhoneNumber?: string;
  receiverEmail?: string;
  senderEmail?: string;
  status: 'pending' | 'success' | 'failed' | 'processing' | 'cancelled' | 'skipped' | 'dispatched';
  message: string;
  timestamp: string;
  error?: string;
  addedAt?: string;
  smsCreditCost?: number;
  analytics?: {
    _id?: string;
    deliveryTimestamp?: string;
    readTimestamp?: string;
    deliveryWebhookPayload?: any;
    deliveryWebhookReceivedAt?: string;
  };
}

interface PaginatedResponse {
  docs: ExecutionLogEntry[];
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

const SORT_BY_OPTIONS = [
  { value: 'timestamp', label: 'Timestamp' },
  { value: 'status', label: 'Status' },
  { value: 'readTimestamp', label: 'Read Timestamp' },
  { value: 'deliveryTimestamp', label: 'Delivery Timestamp' },
];
const SORT_ORDER_OPTIONS = [
  { value: 'desc', label: 'DESC' },
  { value: 'asc', label: 'ASC' },
];

const DEFAULT_PAGE_SIZE = 20;

const getExecutionStatusChip = (status: ExecutionLogEntry['status']) => {
  const statusConfig = {
    pending: { color: 'warning' as const, icon: <ScheduleIcon />, label: 'Pending' },
    processing: { color: 'info' as const, icon: <PlayArrowIcon />, label: 'Processing' },
    success: { color: 'success' as const, icon: <CheckCircleIcon />, label: 'Success' },
    failed: { color: 'error' as const, icon: <FailedIcon />, label: 'Failed' },
    cancelled: { color: 'error' as const, icon: <CancelIcon />, label: 'Cancelled' },
    skipped: { color: 'default' as const, icon: <CloseIcon />, label: 'Skipped' },
    dispatched: { color: 'info' as const, icon: <PlayArrowIcon />, label: 'Dispatched' },
  };
  const cfg = statusConfig[status];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1, py: 0.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      {cfg.icon}
      <Typography variant="caption" color={`${cfg.color}.main` as any}>{cfg.label}</Typography>
    </Box>
  );
};

const formatTs = (ts: string | undefined) => {
  if (!ts) return '-';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

interface ExecutionLogPanelProps {
  campaignId: string;
  status: string;
  onOpenLog: (logId: string) => void;
  templateType: string | null;
}

export const ExecutionLogPanel: React.FC<ExecutionLogPanelProps> = ({ campaignId, status, onOpenLog, templateType }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [logs, setLogs] = useState<ExecutionLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'tile' | 'table'>('tile');
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const isMail = String(templateType || '').toLowerCase() === 'mail';
  const tplLower = String(templateType || '').toLowerCase();
  const isSms = tplLower === 'sms' || tplLower === 'gsmtext';

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery]);

  const fetchLogs = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(rowsPerPage),
        sortBy,
        sortOrder,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);

      const resp = await apiClient.get(`/iu/campaignrunner/${campaignId}/log?${params.toString()}`);
      const data = resp.data as PaginatedResponse;
      setLogs(data.docs || []);
      setTotalCount(data.totalCount || 0);
      setTotalPages(data.totalPages || 0);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch execution logs';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [campaignId, page, rowsPerPage, sortBy, sortOrder, debouncedSearch]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const tableColumns = useMemo(() => {
    const base = isMail
      ? [
          { key: 'timestamp', label: 'Timestamp' },
          { key: 'status', label: 'Status' },
          { key: 'contactId', label: 'Contact ID' },
          { key: 'receiverEmail', label: 'Receiver Email' },
          { key: 'message', label: 'Message' },
          { key: 'error', label: 'Error' },
        ]
      : [
          { key: 'timestamp', label: 'Timestamp' },
          { key: 'status', label: 'Status' },
          { key: 'contactId', label: 'Contact ID' },
          { key: 'senderPhoneNumber', label: 'Sender Phone' },
          { key: 'receiverPhoneNumber', label: 'Receiver Phone' },
          { key: 'message', label: 'Message' },
          { key: 'error', label: 'Error' },
        ];
    if (isSms) base.push({ key: 'smsCreditCost', label: 'SMS Cost' });
    base.push({ key: 'deliveryTimestamp', label: 'Delivery Time' });
    base.push({ key: 'readTimestamp', label: 'Read Time' });
    return base;
  }, [isMail, isSms]);

  const toolbarBg = theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)';

  const handlePageChange = (_e: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleLoadMore = () => {
    setRowsPerPage(prev => prev + DEFAULT_PAGE_SIZE);
  };

  const remainingTileCount = totalCount - (page - 1) * rowsPerPage - logs.length;
  const shownSoFar = (page - 1) * rowsPerPage + logs.length;

  const renderTileView = () => (
    <>
      {logs.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
          {debouncedSearch ? 'No logs match your search.' : 'No execution logs available yet.'}
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" gap={2}>
        {logs.map((log: any, index: number) => (
          <Box
            key={log._id || index}
            onClick={() => log._id && onOpenLog(log._id)}
            sx={{
              width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.33% - 11px)' },
              cursor: 'pointer',
              p: 2,
              borderRadius: 2,
              bgcolor: toolbarBg,
              border: '1px solid',
              borderColor: 'divider',
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: 4 },
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              {getExecutionStatusChip(log.status)}
              <Typography variant="caption" color="text.secondary">{formatTs(log.timestamp)}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }} noWrap>
              {log.contactId}
            </Typography>
            {isMail ? (
              log.receiverEmail && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.5 }}>
                  {log.receiverEmail}
                </Typography>
              )
            ) : (
              <>
                {log.receiverPhoneNumber && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.5 }}>
                    To: {log.receiverPhoneNumber}
                  </Typography>
                )}
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, maxHeight: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {log.message}
            </Typography>
            {log.error && (
              <Typography variant="caption" color="error.main" noWrap sx={{ display: 'block', mb: 0.5 }}>
                {log.error}
              </Typography>
            )}
            {(log.analytics?.deliveryTimestamp || log.analytics?.readTimestamp) && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                {log.analytics.deliveryTimestamp && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Delivered: {formatTs(log.analytics.deliveryTimestamp)}
                  </Typography>
                )}
                {log.analytics.readTimestamp && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Read: {formatTs(log.analytics.readTimestamp)}
                  </Typography>
                )}
              </Box>
            )}
            {isSms && log.smsCreditCost != null && (log.status === 'success' || log.status === 'dispatched' || log.status === 'pending') && (
              <Chip
                icon={<LocalOfferIcon />}
                label={log.smsCreditCost}
                size="small"
                sx={{
                  mt: 1,
                  fontWeight: 700,
                  ...(log.status === 'success' || log.status === 'dispatched'
                    ? { bgcolor: theme.palette.success.main, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }
                    : { bgcolor: theme.palette.warning.main, color: '#000', '& .MuiChip-icon': { color: '#000' } }),
                }}
              />
            )}
          </Box>
        ))}
      </Box>
      {logs.length > 0 && (
        <Box display="flex" flexDirection="column" alignItems="center" mt={2} gap={1}>
          {page < totalPages ? (
            <Button variant="outlined" color="secondary" onClick={handleLoadMore} disabled={loading}>
              {loading ? 'Loading...' : `Load More (${Math.min(totalCount - shownSoFar, DEFAULT_PAGE_SIZE)} of ${totalCount - shownSoFar} remaining)`}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Showing all {totalCount} logs
            </Typography>
          )}
        </Box>
      )}
    </>
  );

  const renderTableView = () => (
    <>
      {logs.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
          {debouncedSearch ? 'No logs match your search.' : 'No execution logs available yet.'}
        </Typography>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', border: '1px solid', borderColor: theme.palette.divider }}>
        <Table stickyHeader aria-label="Execution Logs Table">
          <TableHead>
            <TableRow>
              {tableColumns.map((column) => (
                <TableCell
                  key={column.key}
                  onClick={() => {
                    if (sortBy === column.key) {
                      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy(column.key);
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                  sx={{ cursor: 'pointer', userSelect: 'none', textTransform: 'uppercase' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {column.label}
                    {sortBy === column.key && (
                      <Box component="span" sx={{ marginLeft: 1 }}>{sortOrder === 'asc' ? '▲' : '▼'}</Box>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log: any, index: number) => (
              <TableRow key={log._id || index}>
                <TableCell>{formatTs(log.timestamp)}</TableCell>
                <TableCell>{getExecutionStatusChip(log.status)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log.contactId}</Typography>
                </TableCell>
                {isMail ? (
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log.receiverEmail}</Typography>
                  </TableCell>
                ) : (
                  <>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log.senderPhoneNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{log.receiverPhoneNumber}</Typography>
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ maxWidth: 200, wordBreak: 'break-word', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => log._id && onOpenLog(log._id)}
                    title="View full message"
                  >
                    {log.message}
                  </Typography>
                </TableCell>
                <TableCell>
                  {log.error ? (
                    <Typography variant="body2" color="error.main" sx={{ maxWidth: 200, wordBreak: 'break-word' }}>{log.error}</Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">-</Typography>
                  )}
                </TableCell>
                {isSms && (
                  <TableCell>
                    {log.smsCreditCost != null && (log.status === 'success' || log.status === 'dispatched' || log.status === 'pending') ? (
                      <Chip
                        icon={<LocalOfferIcon />}
                        label={log.smsCreditCost}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          ...(log.status === 'success' || log.status === 'dispatched'
                            ? { bgcolor: theme.palette.success.main, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }
                            : { bgcolor: theme.palette.warning.main, color: '#000', '& .MuiChip-icon': { color: '#000' } }),
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                )}
                <TableCell>{formatTs(log.analytics?.deliveryTimestamp)}</TableCell>
                <TableCell>{formatTs(log.analytics?.readTimestamp)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </>
  );

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.heading?.main ?? theme.palette.secondary.main }}>
            Execution Logs
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {(status as string) === 'running' && (
              <IconButton onClick={fetchLogs} disabled={loading} size="small" title="Refresh execution logs">
                <RefreshIcon />
              </IconButton>
            )}
            {loading && <CircularProgress size={20} />}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            alignItems: 'center',
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            backgroundColor: toolbarBg,
          }}
        >
          <TextField
            size="small"
            placeholder="Search execution logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, maxWidth: { xs: 'none', md: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {isMobile && (
            <IconButton
              onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
              size="large"
              sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {!isMobile && (
            <>
              <FormControl size="small" sx={{ minWidth: 160, ml: 'auto' }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                >
                  {SORT_BY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Sort Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="Sort Order"
                  onChange={(e) => { setSortOrder(e.target.value as 'asc' | 'desc'); setPage(1); }}
                >
                  {SORT_ORDER_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, newMode) => { if (newMode !== null) { setViewMode(newMode); setPage(1); setRowsPerPage(DEFAULT_PAGE_SIZE); } }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    border: '1px solid',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: (t) => t.palette.mode === 'dark' ? '#191f45' : 'secondary.main',
                      color: (t) => t.palette.mode === 'dark' ? '#fff' : 'secondary.contrastText',
                      '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? '#232a54' : 'secondary.dark' },
                    },
                  },
                }}
              >
                <ToggleButton value="tile" aria-label="Tile view">
                  <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                </ToggleButton>
                <ToggleButton value="table" aria-label="Table view">
                  <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
        </Box>

        {/* Mobile menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={() => setMobileMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { mt: 1, minWidth: 240, p: 1.5, boxShadow: theme.shadows[8] } }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 0.5, display: 'block', fontWeight: 600 }}>
            Sort By
          </Typography>
          <FormControl size="small" fullWidth sx={{ px: 1, mb: 1.5 }}>
            <Select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            >
              {SORT_BY_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 0.5, display: 'block', fontWeight: 600 }}>
            Sort Order
          </Typography>
          <FormControl size="small" fullWidth sx={{ px: 1, mb: 1.5 }}>
            <Select
              value={sortOrder}
              onChange={(e) => { setSortOrder(e.target.value as 'asc' | 'desc'); setPage(1); }}
            >
              {SORT_ORDER_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 0.5, display: 'block', fontWeight: 600 }}>
            Display Format
          </Typography>
          <Box sx={{ px: 1 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, newMode) => { if (newMode !== null) { setViewMode(newMode); setPage(1); setRowsPerPage(DEFAULT_PAGE_SIZE); setMobileMenuAnchor(null); } }}
              size="small"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid',
                  borderColor: 'divider',
                  flex: 1,
                  '&.Mui-selected': {
                    backgroundColor: (t) => t.palette.mode === 'dark' ? '#191f45' : 'secondary.main',
                    color: (t) => t.palette.mode === 'dark' ? '#fff' : 'secondary.contrastText',
                    '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? '#232a54' : 'secondary.dark' },
                  },
                },
              }}
            >
              <ToggleButton value="tile" aria-label="Tile view">
                <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} /> Tiles
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} /> Table
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Menu>

        {viewMode === 'table' ? renderTableView() : renderTileView()}
      </CardContent>
    </Card>
  );
};

export default ExecutionLogPanel;
